import { create } from "zustand";

interface PageSelectionState {
  selectedPageIds: string[];
  lastSelectedPageId: string | null;
  anchorPageId: string | null;

  handlePageClick: (
    pageId: string,
    modifier: "none" | "shift" | "cmd",
    orderedPageIds: string[]
  ) => void;
  selectPage: (pageId: string) => void;
  togglePageSelection: (pageId: string) => void;
  selectRange: (
    startPageId: string,
    endPageId: string,
    orderedPageIds: string[]
  ) => void;
  clearSelection: () => void;
  isPageSelected: (pageId: string) => boolean;
  getSelectedCount: () => number;
  isMultiSelectActive: () => boolean;
}

export const usePageSelectionStore = create<PageSelectionState>((set, get) => ({
  selectedPageIds: [],
  lastSelectedPageId: null,
  anchorPageId: null,

  handlePageClick: (
    pageId: string,
    modifier: "none" | "shift" | "cmd",
    orderedPageIds: string[]
  ) => {
    const state = get();

    switch (modifier) {
      case "none":
        state.selectPage(pageId);
        break;
      case "shift":
        if (state.anchorPageId) {
          state.selectRange(state.anchorPageId, pageId, orderedPageIds);
        } else {
          state.selectPage(pageId);
        }
        break;
      case "cmd":
        state.togglePageSelection(pageId);
        break;
    }
  },

  selectPage: (pageId: string) => {
    set({
      selectedPageIds: [pageId],
      lastSelectedPageId: pageId,
      anchorPageId: pageId,
    });
  },

  togglePageSelection: (pageId: string) => {
    set((state) => {
      const isSelected = state.selectedPageIds.includes(pageId);
      const newSelectedPageIds = isSelected
        ? state.selectedPageIds.filter((id) => id !== pageId)
        : [...state.selectedPageIds, pageId];

      return {
        selectedPageIds: newSelectedPageIds,
        lastSelectedPageId: pageId,
        anchorPageId: isSelected ? state.anchorPageId : pageId,
      };
    });
  },

  selectRange: (
    startPageId: string,
    endPageId: string,
    orderedPageIds: string[]
  ) => {
    const startIndex = orderedPageIds.indexOf(startPageId);
    const endIndex = orderedPageIds.indexOf(endPageId);

    if (startIndex === -1 || endIndex === -1) {
      return;
    }

    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    const rangePageIds = orderedPageIds.slice(minIndex, maxIndex + 1);

    set((state) => ({
      selectedPageIds: rangePageIds,
      lastSelectedPageId: endPageId,
      anchorPageId: state.anchorPageId,
    }));
  },

  clearSelection: () => {
    set({
      selectedPageIds: [],
      lastSelectedPageId: null,
      anchorPageId: null,
    });
  },

  isPageSelected: (pageId: string) => {
    return get().selectedPageIds.includes(pageId);
  },

  getSelectedCount: () => {
    return get().selectedPageIds.length;
  },

  isMultiSelectActive: () => {
    return get().selectedPageIds.length > 1;
  },
}));
